import { ReactNode, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { useLogoutSync } from '@crossnokaye/ux-common';

import { resetLabsAssignment } from '@/features/labs/prototype-assignment';
import { isLocalAtlasHost } from '@/helpers/atlas-host';
import { useAppConfig } from '@/hooks/use-app-config';
import { useLoginAndReturn } from '@/hooks/use-login-and-return';
import { useViewOnlyState } from '@/hooks/use-view-only-state';
import { reloadApp } from '@/utils/app-reload';

import { AuthContext, AuthContextValue } from './types';

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const {
    isLoading,
    isAuthenticated,
    error,
    user,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const { data: appConfig } = useAppConfig();

  // Login redirect that returns the user to the page they were on.
  const loginAndReturn = useLoginAndReturn();

  const { isViewOnly, requestViewOnlyToken, requestActiveToken } = useViewOnlyState(
    isAuthenticated,
    getAccessTokenSilently,
    loginAndReturn,
    { skipSilentAuthOnFocus: isLocalAtlasHost(window.location.hostname) }
  );

  const clientId = appConfig?.auth0?.clientId;

  // Set up logout synchronization across apps and tabs
  const { broadcastLogout } = useLogoutSync({
    clientId,
    isAuthenticated,
    isLoading,
    onLogoutDetected: () => {
      resetLabsAssignment();
      reloadApp();
    },
  });

  const login = useCallback(() => loginAndReturn({ prompt: 'login' }), [loginAndReturn]);

  const logout = useCallback(() => {
    // The next person on this browser shouldn't inherit this user's prototype or opt-out.
    resetLabsAssignment();

    // Broadcast logout event for instant tab synchronization
    broadcastLogout();

    // Perform normal logout with redirect
    // Auth0 will automatically remove the auth0.is.authenticated cookie
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout, broadcastLogout]);

  const getAccessToken = useCallback(async () => {
    return await getAccessTokenSilently();
  }, [getAccessTokenSilently]);

  const contextValue: AuthContextValue = useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      error,
      user: user || undefined,
      login,
      logout,
      getAccessToken,
      isViewOnly,
      requestViewOnly: requestViewOnlyToken,
      requestActive: requestActiveToken,
    }),
    [
      isLoading,
      isAuthenticated,
      error,
      user,
      login,
      logout,
      getAccessToken,
      isViewOnly,
      requestViewOnlyToken,
      requestActiveToken,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
